import type { Campaign, DashboardData, FunnelCopy, Platform } from '../types'

const API_BASE = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? 'https://api.goldsaju.com' : '/api')

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`)
    }

    const json = await response.json()
    // Backend returns { success: true, data: {...}, timestamp: ... }
    return json.data || json
  }

  // Dashboard
  async getDashboard(): Promise<DashboardData> {
    return this.request<DashboardData>('/dashboard')
  }

  async refreshMetrics(): Promise<{ success: boolean }> {
    return this.request('/dashboard/refresh', { method: 'POST' })
  }

  // Campaigns
  async getCampaigns(): Promise<Campaign[]> {
    return this.request<Campaign[]>('/campaigns')
  }

  async createCampaign(data: {
    name: string
    platforms: string[]
    channels: string[]
    duration_days: number
    budget: number
  }): Promise<Campaign> {
    return this.request<Campaign>('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateCampaignStatus(
    campaignId: string,
    status: 'active' | 'paused' | 'completed'
  ): Promise<Campaign> {
    return this.request<Campaign>(`/campaigns/${campaignId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
  }

  async deleteCampaign(campaignId: string): Promise<{ success: boolean }> {
    return this.request(`/campaigns/${campaignId}`, { method: 'DELETE' })
  }

  // Funnel Generation
  async generateFunnelCopy(type: string, topic: string): Promise<FunnelCopy> {
    return this.request<FunnelCopy>('/funnel/generate', {
      method: 'POST',
      body: JSON.stringify({ type, topic }),
    })
  }

  // Platforms
  async getPlatforms(): Promise<Platform[]> {
    return this.request<Platform[]>('/platforms')
  }

  // Analytics
  async getHeatmapData(metric: 'revenue' | 'conversion_rate'): Promise<Array<{ platform: string; value: number }>> {
    return this.request(`/analytics/heatmap?metric=${metric}`)
  }

  async getTimeSeriesData(days: number = 7): Promise<Array<{ date: string; revenue: number; conversions: number }>> {
    return this.request(`/analytics/timeseries?days=${days}`)
  }
}

export const api = new ApiService()
